import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../lib/i18n';
import { frameInVideo, type FoundVin } from '../../lib/vin';
import type { PlateReading } from '../../lib/vin-paddle';

/** How often a frame is checked for a barcode, and how often the slower text reader is run. */
const BARCODE_EVERY_MS = 300;
const TEXT_EVERY_MS = 700;
/** The frame on screen: wide and low, the shape of a VIN plate or a door-jamb sticker. */
const FRAME_WIDTH = 0.88;
const FRAME_ASPECT = 3.4;

/** How many recent readings are kept to agree with each other. */
const RECENT_READINGS = 6;

/**
 * A reading is taken only once the camera keeps seeing it: twice when its
 * check digit adds up, three times otherwise — and only when the recogniser
 * was sure of it. One frame's guess is never enough.
 */
function settled(recent: PlateReading[], latest: PlateReading): boolean {
  const same = recent.filter((r) => r.vin === latest.vin);
  const sure = same.reduce((t, r) => t + r.confidence, 0) / same.length;
  if (latest.verified) return same.length >= 2 && sure >= 0.85;
  return same.length >= 3 && sure >= 0.9;
}

export interface ScanResult {
  photo: File;
  /** Null when the shutter was pressed before anything was read; the photo is then read as a whole. */
  vin: FoundVin | null;
}

/**
 * The camera with a frame to put the VIN in, read continuously until it is
 * certain: a barcode at once, text once its check digit adds up or the same
 * seventeen characters come back twice. Then the frame is kept as the photo.
 */
export function VinScanner({ onDone, onFallback, onClose }: {
  onDone: (result: ScanResult) => void;
  /** The camera could not be opened: take the photo the ordinary way instead. */
  onFallback: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const video = useRef<HTMLVideoElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [reading, setReading] = useState<string | null>(null);
  const finished = useRef(false);
  const capture = useRef<(vin: FoundVin | null) => void>(() => undefined);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let cancelled = false;

    const stop = () => {
      cancelled = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };

    capture.current = (vin) => {
      const v = video.current;
      if (finished.current || !v || v.videoWidth === 0) return;
      finished.current = true;
      const canvas = document.createElement('canvas');
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext('2d')!.drawImage(v, 0, 0);
      stop();
      canvas.toBlob((blob) => {
        if (blob) onDone({ photo: new File([blob], 'vin.jpg', { type: 'image/jpeg' }), vin });
      }, 'image/jpeg', 0.92);
    };

    /** The part of the picture inside the frame, in the video's own pixels. */
    const crop = (): HTMLCanvasElement | null => {
      const v = video.current;
      const box = frame.current;
      if (!v || !box || v.videoWidth === 0) return null;
      const area = frameInVideo(v.getBoundingClientRect(), box.getBoundingClientRect(), v.videoWidth, v.videoHeight);
      // Before the first real frame the camera reports a picture too small to hold the frame.
      if (area.width < 1 || area.height < 1) return null;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(area.width);
      canvas.height = Math.round(area.height);
      canvas.getContext('2d', { willReadFrequently: true })!
        .drawImage(v, area.left, area.top, area.width, area.height, 0, 0, canvas.width, canvas.height);
      return canvas;
    };

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
      } catch {
        if (!cancelled) setFailed(true);
        return;
      }
      if (cancelled) { stream.getTracks().forEach((track) => track.stop()); return; }
      const v = video.current!;
      v.srcObject = stream;
      await v.play().catch(() => undefined);

      const [{ readVinBarcode }, plate] = await Promise.all([
        import('../../lib/vin-barcode'),
        import('../../lib/vin-paddle'),
      ]);
      const reader = await plate.prepareVinReader().then(() => plate.readPlate, () => null);
      const seen: PlateReading[] = [];
      let lastText = 0;
      let textBusy = false;

      const tick = async () => {
        if (cancelled || finished.current) return;
        const picture = crop();
        if (picture) {
          const context = picture.getContext('2d')!;
          const barcode = await readVinBarcode(context.getImageData(0, 0, picture.width, picture.height)).catch(() => null);
          if (barcode) { capture.current(barcode); return; }

          if (reader && !textBusy && Date.now() - lastText >= TEXT_EVERY_MS) {
            textBusy = true;
            lastText = Date.now();
            void reader(picture).then((found) => {
              textBusy = false;
              if (!found || cancelled || finished.current) return;
              setReading(found.vin);
              seen.splice(0, Math.max(0, seen.length - (RECENT_READINGS - 1)));
              seen.push(found);
              if (settled(seen, found)) capture.current({ vin: found.vin, verified: found.verified });
            }, () => { textBusy = false; });
          }
        }
        timer = window.setTimeout(() => void tick(), BARCODE_EVERY_MS);
      };
      void tick();
    };

    void start();
    return stop;
    // Opens the camera once for the life of the scanner.
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black text-white">
      <video ref={video} playsInline muted autoPlay className="absolute inset-0 h-full w-full object-cover" />

      <div
        ref={frame}
        className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 rounded-xl border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
        style={{ width: `${FRAME_WIDTH * 100}vw`, height: `${(FRAME_WIDTH * 100) / FRAME_ASPECT}vw` }}
      />

      <div className="absolute inset-x-0 top-[calc(38%+16vw)] px-6 text-center">
        {failed ? (
          <p className="text-sm text-white/80">{t('vin.noCamera')}</p>
        ) : (
          <p className="tabular font-mono text-lg tracking-wider text-white/90">{reading ?? 'VIN'}</p>
        )}
      </div>

      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-4 px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-4">
        <button onClick={onClose} className="min-h-11 min-w-20 rounded-xl px-3 text-sm font-semibold text-white/85">
          {t('common.cancel')}
        </button>
        {failed ? (
          <button onClick={onFallback} className="min-h-12 rounded-xl bg-white px-5 text-sm font-semibold text-black">
            {t('vin.usePhoto')}
          </button>
        ) : (
          <button
            onClick={() => capture.current(null)}
            aria-label={t('vin.shoot')}
            className="grid h-16 w-16 place-items-center rounded-full border-4 border-white/80"
          >
            <span className="h-12 w-12 rounded-full bg-white" />
          </button>
        )}
        <span className="min-w-20" />
      </div>
    </div>,
    document.body,
  );
}
